import { ReplaySubject } from 'rxjs';

import {
  JITMeetConferenceSession,
} from '../../../domain/gateways/jit-meet/jit-meet-conference-session';

import {
  JITMeetConferenceCommand,
  JITMeetConferenceEvent,
  JITMeetConferenceEventType,
  JITMeetRemoteParticipantsChangedData,
} from '../../../domain/models/jit-meet/conference/jit-meet-conference.model';

import {
  JitsiMeetExternalApiInstance,
  JitsiMeetParticipantInfo,
} from './jitsi-meet.types';

interface EventBinding {
  jitsiEvent: string;
  jitMeetEvent: JITMeetConferenceEventType;
  listener: (payload: unknown) => void;
}

/**
 * Adaptador de la instancia de Jitsi a los contratos
 * propios de JIT Meet.
 *
 * La clase:
 * - traduce eventos de Jitsi a eventos de dominio;
 * - ejecuta comandos sobre la conferencia;
 * - controla salida y finalización;
 * - conserva la cantidad real de participantes remotos.
 */
export class JitsiMeetConferenceSession
  extends JITMeetConferenceSession {

  private readonly eventsSubject =
    new ReplaySubject<JITMeetConferenceEvent>(1);

  readonly events$ =
    this.eventsSubject.asObservable();

  private readonly bindings: EventBinding[] = [];

  /**
   * Participantes remotos detectados actualmente.
   *
   * Usamos un Set para evitar duplicados cuando Jitsi
   * emite más de una vez un evento equivalente.
   */
  private readonly remoteParticipantIds =
    new Set<string>();

  private disposed = false;

  private finishingPromise:
    Promise<void> | null = null;

  private localParticipantId:
    string | null = null;

  private localDeafened = false;

  constructor(
    private readonly api:
      JitsiMeetExternalApiInstance
  ) {
    super();

    this.bindEvents();

    this.emit('session-initialized');
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  execute(
    command: JITMeetConferenceCommand,
    ...args: readonly unknown[]
  ): void {
    if (this.disposed) {
      throw new Error(
        'La sesión de JIT Meet ya fue finalizada.'
      );
    }
    if (command === 'setLocalDeafened') {
      this.localDeafened = args[0] as boolean;
      const volume = this.localDeafened ? 0 : 1;
      this.remoteParticipantIds.forEach((id) => {
      this.api.executeCommand('setParticipantVolume', id, volume);
      });
      return;
    }
    this.api.executeCommand(
      this.resolveCommand(command),
      ...args
    );
  }

  /**
   * Abandona únicamente la sesión local.
   *
   * Se utiliza principalmente para la salida voluntaria
   * del paciente. No finaliza la sala para los demás.
   */
  leave(): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }

    if (this.finishingPromise) {
      return this.finishingPromise;
    }

    this.finishingPromise =
      new Promise<void>((resolve) => {
        let completed = false;

        const complete = (): void => {
          if (completed) {
            return;
          }

          completed = true;

          clearTimeout(timeoutId);

          this.api.removeEventListener(
            'readyToClose',
            complete
          );

          this.api.removeEventListener(
            'videoConferenceLeft',
            complete
          );

          resolve();
        };

        /*
         * Algunos navegadores móviles no notifican
         * inmediatamente videoConferenceLeft.
         */
        const timeoutId = setTimeout(
          complete,
          3000
        );

        this.api.addEventListener(
          'readyToClose',
          complete
        );

        this.api.addEventListener(
          'videoConferenceLeft',
          complete
        );

        try {
          /*
           * hangup abandona únicamente la conexión local.
           *
           * El paciente nunca debe ejecutar endConference.
           */
          this.api.executeCommand('hangup');
        } catch {
          complete();
        }
      }).finally(() => {
        this.dispose();

        this.finishingPromise = null;
      });

    return this.finishingPromise;
  }

  /**
   * Finaliza la conferencia para todos los participantes.
   *
   * Solo debe ejecutarse desde la sesión del profesional
   * que cuenta con permisos de moderador.
   */
  finish(): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }

    if (this.finishingPromise) {
      return this.finishingPromise;
    }

    this.finishingPromise =
      new Promise<void>((resolve) => {
        let completed = false;

        const complete = (): void => {
          if (completed) {
            return;
          }

          completed = true;

          clearTimeout(timeoutId);

          this.api.removeEventListener(
            'readyToClose',
            complete
          );

          this.api.removeEventListener(
            'videoConferenceLeft',
            complete
          );

          resolve();
        };

        /*
         * Si Jitsi no emite los eventos de cierre,
         * liberamos técnicamente la sesión después
         * de cinco segundos.
         */
        const timeoutId = setTimeout(
          complete,
          5000
        );

        this.api.addEventListener(
          'readyToClose',
          complete
        );

        this.api.addEventListener(
          'videoConferenceLeft',
          complete
        );

        try {
          this.api.executeCommand(
            'endConference'
          );
        } catch {
          /*
           * Respaldo defensivo para versiones o
           * configuraciones donde endConference
           * no esté disponible.
           */
          try {
            this.api.executeCommand('hangup');
          } finally {
            complete();
          }
        }
      }).finally(() => {
        this.dispose();

        this.finishingPromise = null;
      });

    return this.finishingPromise;
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;

    this.bindings.forEach(
      ({ jitsiEvent, listener }) => {
        this.api.removeEventListener(
          jitsiEvent,
          listener
        );
      }
    );

    this.bindings.length = 0;

    this.remoteParticipantIds.clear();
    this.localParticipantId = null;

    this.api.dispose();

    this.eventsSubject.complete();
  }

  private bindEvents(): void {
    this.bind(
      'videoConferenceJoined',
      'conference-joined',
      (payload: unknown) => {
        this.localParticipantId =
          this.readParticipantId(payload);

        /*
         * Conservamos el comportamiento actual:
         * se oculta la tira nativa de participantes.
         */
        this.execute('toggleFilmStrip');

        /*
         * El evento participantJoined no se vuelve
         * a emitir para participantes que ya estaban
         * en la sala.
         *
         * Por eso, después de ingresar, consultamos
         * explícitamente la lista actual.
         */
        setTimeout(() => {
          this.synchronizeRemoteParticipants();
        }, 250);
      }
    );

    this.bind(
      'videoConferenceLeft',
      'conference-left',
      () => {
        this.clearRemoteParticipants();
      }
    );

    this.bind(
      'readyToClose',
      'ready-to-close',
      () => {
        this.clearRemoteParticipants();
      }
    );

    this.bind(
      'participantJoined',
      'participant-joined',
      (payload: unknown) => {
        const participantId =
          this.readParticipantId(payload);

        if (
          participantId &&
          participantId !==
            this.localParticipantId
        ) {
          this.remoteParticipantIds.add(
            participantId
          );
          if (this.localDeafened) {
            this.api.executeCommand('setParticipantVolume', participantId, 0);
          }
        }

        this.emitRemoteParticipantsChanged();
      }
    );

    this.bind(
      'participantLeft',
      'participant-left',
      (payload: unknown) => {
        const participantId =
          this.readParticipantId(payload);

        if (participantId) {
          this.remoteParticipantIds.delete(
            participantId
          );
        }

        this.emitRemoteParticipantsChanged();
      }
    );

    this.bind(
      'audioMuteStatusChanged',
      'audio-mute-changed'
    );

    this.bind(
      'videoMuteStatusChanged',
      'video-mute-changed',
      (payload: any) => {
        const isMuted = this.readMutedValue(payload);

        if (!isMuted) {
          setTimeout(() => {
            try {
              this.api.executeCommand('setVirtualBackground', {
                backgroundEffectEnabled: true,
                backgroundType: 'blur',
                blurValue: 8
              });
            } catch (error) {
              console.warn('JIT Meet: No fue posible aplicar desenfoque automático', error);
            }
          }, 500);
        }
      }
    );

    this.bind(
      'screenSharingStatusChanged',
      'screen-sharing-changed'
    );

    this.bind(
      'cameraError',
      'camera-error'
    );

    this.bind(
      'micError',
      'microphone-error'
    );
  }

  private bind(
    jitsiEvent: string,
    jitMeetEvent: JITMeetConferenceEventType,
    beforeEmit?: (payload: unknown) => void
  ): void {
    const listener =
      (payload: unknown): void => {
        beforeEmit?.(payload);

        this.emit(
          jitMeetEvent,
          payload
        );
      };

    this.bindings.push({
      jitsiEvent,
      jitMeetEvent,
      listener,
    });

    this.api.addEventListener(
      jitsiEvent,
      listener
    );
  }

  /**
   * Consulta la lista actual de participantes,
   * incluyendo los que estaban antes del ingreso
   * del usuario local.
   */
  private synchronizeRemoteParticipants(): void {
    this.remoteParticipantIds.clear();

    const participants =
      this.api.getParticipantsInfo?.() ?? [];

    participants.forEach(
      (
        participant:
          JitsiMeetParticipantInfo
      ) => {
        const participantId =
          this.readParticipantId(
            participant
          );

        if (
          !participantId ||
          participantId ===
            this.localParticipantId
        ) {
          return;
        }

        this.remoteParticipantIds.add(
          participantId
        );
      }
    );

    this.emitRemoteParticipantsChanged();
  }

  private clearRemoteParticipants(): void {
    this.remoteParticipantIds.clear();

    this.emitRemoteParticipantsChanged();
  }

  private emitRemoteParticipantsChanged():
    void {

    const data:
      JITMeetRemoteParticipantsChangedData = {
        count:
          this.remoteParticipantIds.size,
      };

    this.emit(
      'remote-participants-changed',
      data
    );
  }

  private readParticipantId(
    payload: unknown
  ): string | null {
    if (
      typeof payload !== 'object' ||
      payload === null
    ) {
      return null;
    }

    const record =
      payload as Record<string, unknown>;

    const rawValue =
      record['id'] ??
      record['participantId'];

    if (
      typeof rawValue !== 'string'
    ) {
      return null;
    }

    const participantId =
      rawValue.trim();

    return participantId.length > 0
      ? participantId
      : null;
  }

  private readMutedValue(payload: unknown): boolean {
    if (typeof payload === 'boolean') {
      return payload;
    }
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'muted' in payload
    ) {
      return Boolean(
        (payload as Record<string, unknown>)['muted']
      );
    }
    return false;
  }

  private emit(
    type: JITMeetConferenceEventType,
    data?: unknown
  ): void {
    this.eventsSubject.next({
      type,
      occurredAt: new Date(),
      data,
    });
  }

  private resolveCommand(
    command: JITMeetConferenceCommand
  ): string {
    const commandMap:
      Record<
        JITMeetConferenceCommand,
        string
      > = {
        hangup: 'hangup',
        endConference: 'endConference',

        toggleAudio: 'toggleAudio',
        toggleVideo: 'toggleVideo',
        toggleChat: 'toggleChat',
        toggleShareScreen:
          'toggleShareScreen',

        toggleFilmStrip:
          'toggleFilmStrip',
        toggleTileView:
          'toggleTileView',

        setAudioMuted:
          'setAudioMuted',
        setVideoMuted:
          'setVideoMuted',

        displayName: 'displayName',
        toggleVirtualBackground: 'toggleVirtualBackgroundDialog',
        setLocalDeafened: 'setLocalDeafened',
      };

    return commandMap[command];
  }
}
